"use client";

import { useState } from "react";

interface PricingPlan {
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  period: string;
  desc: string;
  features: string[];
  cta: string;
  href: string;
  popular: boolean;
  badge: string | null;
  gradient: boolean;
}

interface PricingSectionProps {
  plans: PricingPlan[];
}

export default function PricingSection({ plans }: PricingSectionProps) {
  const [annual, setAnnual] = useState(false);

  return (
    <>
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
          Simple, Transparent Pricing
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
          Growth includes a 14-day free trial. No credit card required to start.
        </p>

        {/* Monthly/Annual Toggle */}
        <div className="inline-flex items-center gap-3 bg-gray-100 dark:bg-gray-800 rounded-full p-1.5">
          <button
            onClick={() => setAnnual(false)}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
              !annual
                ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                : "text-gray-400 hover:text-gray-700"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setAnnual(true)}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${
              annual
                ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                : "text-gray-400 hover:text-gray-700"
            }`}
          >
            Annual
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full transition-all ${
              annual
                ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300"
                : "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400"
            }`}>
              Annual
            </span>
          </button>
        </div>

        {/* Savings callout */}
        {annual && (
          <div className="mt-4 inline-flex items-center gap-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 text-sm font-medium px-4 py-2 rounded-full animate-in fade-in duration-300">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            Annual billing lowers the monthly equivalent and keeps the rate fixed for the term
          </div>
        )}
      </div>

      {/* Mobile comparison hint */}
      <div className="mb-4 text-center 2xl:hidden">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Swipe or scroll horizontally to compare plans on smaller screens.
        </p>
      </div>

      <div id="pricing-scroll-container" className="flex gap-5 mx-auto overflow-x-auto pb-6 pt-5 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent px-4 2xl:grid 2xl:grid-cols-5 2xl:overflow-visible 2xl:pb-0 2xl:px-0 2xl:max-w-[1400px]">
        {plans.map((plan, i) => {
          const price = annual ? plan.annualPrice : plan.monthlyPrice;
          const displayPrice = `$${price}`;
          const displayPeriod = "/month";
          const monthlySavings = plan.monthlyPrice - plan.annualPrice;
          const yearlySavings = monthlySavings * 12;

          return (
          <div
            key={i}
            className={`rounded-xl p-6 border-2 h-full flex flex-col w-[280px] min-w-[280px] 2xl:w-auto 2xl:min-w-0 snap-center ${
              plan.gradient
                ? "border-purple-500 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/20 relative"
                : plan.popular
                ? "border-blue-600 bg-blue-50/50 dark:bg-blue-900/20 relative"
                : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
            }`}
          >
            {plan.badge && (
              <div className={`absolute -top-4 left-1/2 -translate-x-1/2 text-white text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap shadow-lg z-10 ${
                plan.gradient
                  ? "bg-gradient-to-r from-purple-600 to-blue-600 shadow-purple-500/30"
                  : "bg-blue-600 shadow-blue-500/30"
              }`}>
                {plan.badge}
              </div>
            )}
            <h3 className={`text-lg font-semibold ${plan.gradient ? "text-purple-900 dark:text-purple-200" : "text-gray-900 dark:text-white"}`}>
              {plan.name}
            </h3>
            <div className="flex items-baseline gap-1 mt-3 mb-1">
              {annual && plan.monthlyPrice > 0 && (
                <span className="text-lg text-gray-400 line-through mr-1">
                  ${plan.monthlyPrice}
                </span>
              )}
              <span className={`text-4xl font-bold ${plan.gradient ? "text-purple-900 dark:text-white" : "text-gray-900 dark:text-white"}`}>
                {displayPrice}
              </span>
              <span className="text-gray-400">{displayPeriod}</span>
            </div>

            {/* Savings badge — only for paid plans when annual */}
            {annual && yearlySavings > 0 && (
              <div className="mb-2">
                <span className="inline-flex items-center gap-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-bold px-2.5 py-1 rounded-full">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                  Save ${yearlySavings}/year
                </span>
              </div>
            )}

            {/* No savings = spacer */}
            {(!annual || yearlySavings === 0) && <div className="mb-2" />}

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {plan.desc}
            </p>
            <ul className="space-y-2.5 mb-6 flex-1">
              {plan.features.map((f, j) => (
                <li
                  key={j}
                  className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
                >
                  <svg className={`w-4 h-4 flex-shrink-0 ${plan.gradient ? "text-purple-500" : "text-green-500"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  {f}
                </li>
              ))}
            </ul>
            <a
              href={plan.href}
              className={`block text-center py-2.5 rounded-lg font-medium text-sm transition mt-auto ${
                plan.gradient
                  ? "bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg shadow-purple-500/25"
                  : plan.popular
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
              }`}
            >
              {plan.cta}
            </a>
          </div>
          );
        })}
      </div>
    </>
  );
}
